/**
 * Bundle Builder Web Component
 * Allows users to select products from a collection and add them as a bundle to cart
 */

class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.collectionHandle = this.dataset.collection;
    this.giftCollectionHandle = this.dataset.giftCollection;
    this.minItems = parseInt(this.dataset.minItems, 10) || 3;
    this.itemsPerPage = parseInt(this.dataset.itemsPerPage, 10) || 24;
    this.productTitle = this.dataset.productTitle || 'Pack';
    this.currentProductId = this.dataset.productId;
    this.sectionId = this.dataset.sectionId;

    this.selectedItems = new Map();
    this.allProducts = [];
    this.currentPage = 1;
    this.hasMoreProducts = true;
    this.isLoading = false;

    this.giftProducts = [];
    this.giftCurrentPage = 1;
    this.hasMoreGifts = true;
    this.selectedGift = null;
    this.isGiftLoading = false;

    this.discountTiers = [
      { quantity: 3, discount: 5, type: 'fixed' },
      { quantity: 5, discount: 10, type: 'fixed' },
      { quantity: 10, discount: 0, type: 'gift' }
    ];

    // Track which milestones have been reached (for PostHog tracking)
    this.reachedMilestones = new Set();
  }

  connectedCallback() {
    this.productsGrid = this.querySelector(`#BundleProductsGrid-${this.sectionId}`);
    this.paginationContainer = this.querySelector(`#BundlePagination-${this.sectionId}`);
    this.giftCollapsible = this.querySelector(`#BundleGiftCollapsible-${this.sectionId}`);
    this.giftLockedOverlay = this.querySelector(`#BundleGiftLocked-${this.sectionId}`);
    this.giftGrid = this.querySelector(`#BundleGiftGrid-${this.sectionId}`);
    this.giftPaginationContainer = this.querySelector(`#BundleGiftPagination-${this.sectionId}`);
    this.summaryContainer = document.querySelector(`#BundleSummary-${this.sectionId}`);
    this.selectedItemsContainer = document.querySelector(`#BundleSelectedItems-${this.sectionId}`);
    this.giftSelectedContainer = document.querySelector(`#BundleGiftSelected-${this.sectionId}`);
    this.mobileAddBtn = document.querySelector(`#BundleMobileAddBtn-${this.sectionId}`);

    // Track total pages and products
    this.totalPages = 1;
    this.giftTotalPages = 1;
    this.giftTotalProducts = 0;

    // Check for preselect query parameter
    const urlParams = new URLSearchParams(window.location.search);
    this.preselectedHandle = urlParams.get('preselect');

    // Store cart items to filter them out from the bundle grid
    this.cartItems = [];

    if (this.collectionHandle) {
      // Fetch cart first, then load products (cart items will be filtered out)
      this.fetchCart().then(() => {
        this.loadProducts().then(() => {
          this.handlePreselection();
        });
      });
    } else {
      this.showError('No collection configured');
    }

    // Always load gift products on init (function handles empty collection case)
    this.loadGiftProducts();

    this.setupEventListeners();

    // Initialize summary with default state (shows "Plus que 3 produits pour -5€")
    this.updateSummary();

    // Initialize gift counter (shows "choisissez encore 10 voitures")
    this.checkGiftEligibility();

    // Initialize delivery date estimate
    this.updateDeliveryDate();

    // Track bundle page view
    if (window.bmTracking) {
      window.bmTracking.bundleView(this.dataset.productHandle || window.location.pathname.split('/').pop(), this.collectionHandle);
    }
  }

  /**
   * Calculate and display estimated delivery date
   * Uses 3 business days (skipping weekends) like the shipping estimator
   */
  updateDeliveryDate() {
    const deliveryDateEl = this.summaryContainer?.querySelector('.bundle-delivery-date');
    if (!deliveryDateEl) return;

    // Get delivery days from settings or default to 4 (conservative estimate)
    const deliveryDays = window.shippingEstimatorSettings?.homeDeliveryDays || 4;

    // Calculate delivery date (skip weekends)
    const deliveryDate = this.addBusinessDays(new Date(), deliveryDays);

    // Format date in French: "mercredi 15 janvier"
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    const formattedDate = deliveryDate.toLocaleDateString('fr-FR', options);

    deliveryDateEl.textContent = formattedDate;
  }

  /**
   * Add business days to a date (skip weekends)
   */
  addBusinessDays(date, days) {
    const result = new Date(date);
    let addedDays = 0;
    while (addedDays < days) {
      result.setDate(result.getDate() + 1);
      const dayOfWeek = result.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        // Skip weekend days (0 = Sunday, 6 = Saturday)
        addedDays++;
      }
    }
    return result;
  }

  setupEventListeners() {
    // Get collapsible references for scroll targets
    this.productsCollapsible = this.querySelector('.bundle-collapsible--products');

    // Products pagination
    this.paginationContainer?.querySelector('.bundle-pagination-prev')?.addEventListener('click', () => {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.loadProducts().then(() => this.scrollToProductsGrid());
      }
    });

    this.paginationContainer?.querySelector('.bundle-pagination-next')?.addEventListener('click', () => {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.loadProducts().then(() => this.scrollToProductsGrid());
      }
    });

    // Gift pagination (uses client-side pagination since all cards are pre-loaded)
    this.giftPaginationContainer?.querySelector('.bundle-pagination-prev')?.addEventListener('click', () => {
      if (this.giftCurrentPage > 1) {
        this.giftCurrentPage--;
        this.renderGiftPage();
        this.scrollToGiftGrid();
      }
    });

    this.giftPaginationContainer?.querySelector('.bundle-pagination-next')?.addEventListener('click', () => {
      if (this.giftCurrentPage < this.giftTotalPages) {
        this.giftCurrentPage++;
        this.renderGiftPage();
        this.scrollToGiftGrid();
      }
    });

    this.summaryContainer?.querySelector('.bundle-add-to-cart')?.addEventListener('click', () => {
      this.addToCart();
    });

    // Mobile add to cart button (in collapsed bar)
    this.mobileAddBtn?.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent triggering the expand/collapse
      this.handleMobileAddToCart();
    });

    // Mobile summary bar toggle
    this.setupMobileSummaryToggle();

    // Listen for theme's quick-view:open event to apply bundle mode
    document.addEventListener('quick-view:open', (e) => {
      // Remove loading state from any view buttons
      this.querySelectorAll('.bundle-card-view.loading').forEach(btn => {
        btn.classList.remove('loading');
      });

      if (this.pendingBundleMode) {
        this.applyBundleModeToQuickView();
        this.pendingBundleMode = false;
      }
    });

    this.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('.bundle-card-toggle');
      if (toggleBtn) {
        const card = toggleBtn.closest('.bundle-card');
        if (card) this.toggleProduct(card);
      }

      const selectBtn = e.target.closest('.bundle-card-select');
      if (selectBtn) {
        const card = selectBtn.closest('.bundle-card--gift');
        if (card) this.selectGift(card);
      }

      const removeBtn = e.target.closest('.bundle-selected-item-remove');
      if (removeBtn) {
        const variantId = removeBtn.dataset.variantId;
        if (variantId) this.removeProduct(variantId);
      }

      // Handle overlay remove button click
      const overlayRemoveBtn = e.target.closest('.bundle-card-selected-overlay .bundle-card-remove');
      if (overlayRemoveBtn) {
        const card = overlayRemoveBtn.closest('.bundle-card');
        if (card) {
          // Check if it's a gift card or regular product card
          if (card.classList.contains('bundle-card--gift')) {
            this.deselectGift(card);
          } else {
            this.toggleProduct(card);
          }
        }
      }

      // Handle Quick View triggers (image and title clicks)
      const quickViewTrigger = e.target.closest('.bundle-quick-view-trigger');
      if (quickViewTrigger) {
        e.preventDefault();
        const handle = quickViewTrigger.dataset.productHandle;
        const variantId = quickViewTrigger.dataset.variantId;

        // Add loading state to the view button if it's a button element
        if (quickViewTrigger.classList.contains('bundle-card-view')) {
          quickViewTrigger.classList.add('loading');
        }

        if (handle) this.openQuickView(handle, variantId);
      }
    });
  }

  openQuickView(handle, variantId) {
    // Set flag to indicate we want bundle mode when Quick View opens
    this.pendingBundleMode = true;

    // Create a temporary quick-view element to trigger the theme's Quick View
    const quickView = document.createElement('quick-view');
    quickView.setAttribute('data-product-handle', handle);
    quickView.setAttribute('data-product-id', variantId);
    quickView.setAttribute('href', '#Product-Modal');
    quickView.style.display = 'none';
    document.body.appendChild(quickView);

    // Trigger click on the quick-view element to open the modal
    quickView.click();

    // Clean up after a short delay
    setTimeout(() => quickView.remove(), 500);
  }

  applyBundleModeToQuickView() {
    const self = this;
    const modal = document.querySelector('#Product-Modal');

    if (!modal) return;

    // Add bundle-mode class to modal
    modal.classList.add('bundle-mode');

    // Function to modify the add to cart button
    const modifyAddToCartButton = () => {
      // Change "Ajouter au panier" to "Ajouter au pack"
      // Note: class uses dashes (single-add-to-cart-button), not underscores
      const addToCartBtns = modal.querySelectorAll('.single-add-to-cart-button, .single_add_to_cart_button');

      addToCartBtns.forEach(btn => {
        const textSpan = btn.querySelector('.single-add-to-cart-button--text');
        if (textSpan && !textSpan.dataset.bundleModified) {
          textSpan.dataset.originalText = textSpan.textContent;
          textSpan.textContent = 'Ajouter au pack';
          textSpan.dataset.bundleModified = 'true';
        }

        // Add click interceptor for bundle mode
        if (!btn.dataset.bundleInterceptor) {
          btn.dataset.bundleInterceptor = 'true';
          btn.addEventListener('click', (e) => {
            if (modal.classList.contains('bundle-mode')) {
              e.preventDefault();
              e.stopPropagation();
              self.addToPackFromQuickView(modal);
            }
          }, true);
        }
      });

      // Also intercept form submission
      const forms = modal.querySelectorAll('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
      forms.forEach(form => {
        if (!form.dataset.bundleInterceptor) {
          form.dataset.bundleInterceptor = 'true';
          form.addEventListener('submit', (e) => {
            if (modal.classList.contains('bundle-mode')) {
              e.preventDefault();
              e.stopPropagation();
              self.addToPackFromQuickView(modal);
            }
          }, true);
        }
      });

      return addToCartBtns.length > 0;
    };

    // Apply immediately (content should be loaded after quick-view:open event)
    if (!modifyAddToCartButton()) {
      // If button not found yet, retry a few times
      setTimeout(modifyAddToCartButton, 100);
      setTimeout(modifyAddToCartButton, 300);
    }
  }

  addToPackFromQuickView(modal) {
    // Get product data from the modal
    const productDetail = modal.querySelector('.thb-product-detail');
    if (!productDetail) return;

    const handle = productDetail.dataset.handle;

    // Find the variant ID from the form or variant selects
    let variantId = null;
    const variantInput = modal.querySelector('input[name="id"]');
    if (variantInput) {
      variantId = variantInput.value;
    }

    if (!variantId || !handle) {
      console.error('Could not find product data in Quick View');
      return;
    }

    // Find the corresponding card in the bundle grid
    const card = this.querySelector(`product-card[data-handle="${handle}"]`);
    if (card) {
      // If it's not already selected, toggle it
      if (!card.classList.contains('bundle-card--selected')) {
        this.toggleProduct(card);
      }

      // Show success feedback
      this.showAddToPackSuccess();

      // Close the modal
      const closeBtn = modal.querySelector('.product-modal__toggle');
      if (closeBtn) closeBtn.click();
    } else {
      // Product not in current view, add manually
      this.addProductToPackManually(variantId, handle, modal);
    }
  }

  addProductToPackManually(variantId, handle, modal) {
    // Get product info from modal
    const titleEl = modal.querySelector('.product--title');
    const priceEl = modal.querySelector('.price .money');
    const imageEl = modal.querySelector('.product-images img');

    const title = titleEl ? titleEl.textContent.trim() : handle;
    const priceText = priceEl ? priceEl.textContent : '0';
    const price = this.parsePriceText(priceText);
    const image = imageEl ? imageEl.src : '';

    // Add to selected items
    if (!this.selectedItems.has(variantId)) {
      this.selectedItems.set(variantId, {
        variantId,
        productId: variantId,
        handle,
        title,
        price,
        image
      });

      this.updateSummary();
      this.showAddToPackSuccess();

      // Close the modal
      const closeBtn = modal.querySelector('.product-modal__toggle');
      if (closeBtn) closeBtn.click();
    }
  }

  parsePriceText(priceText) {
    // Parse price from text like "12,50 €" or "€12.50"
    const cleaned = priceText.replace(/[^\d.,]/g, '').replace(',', '.');
    return Math.round(parseFloat(cleaned) * 100) || 0;
  }

  showAddToPackSuccess() {
    // Create and show a toast notification
    const toast = document.createElement('div');
    toast.className = 'bundle-toast';
    toast.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
      <span>Produit ajouté au pack</span>
    `;
    toast.style.cssText = `
      position: fixed;
      bottom: 100px;
      left: 50%;
      transform: translateX(-50%);
      background: var(--color-accent, #e38a5b);
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      gap: 8px;
      z-index: 9999;
      animation: slideUp 0.3s ease;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `;

    document.body.appendChild(toast);

    // Remove after 2 seconds
    setTimeout(() => {
      toast.style.animation = 'fadeOut 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }

  async loadProducts() {
    if (this.isLoading) return;
    this.isLoading = true;

    // Show loading state
    this.productsGrid.innerHTML = `
      <div class="bundle-loading">
        <div class="bundle-loading-spinner">
          <svg class="thb-loading" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2" opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/></path></svg>
        </div>
        <span>Chargement...</span>
      </div>
    `;

    try {
      // Use Section Rendering API to get pre-rendered HTML cards with full Shopify data
      const url = `/collections/${this.collectionHandle}?section_id=bundle-products-html&page=${this.currentPage}`;
      const response = await fetch(url);

      if (!response.ok) throw new Error('Failed to load products');

      const html = await response.text();

      // Parse the HTML response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const dataContainer = doc.querySelector('.bundle-products-data');

      if (!dataContainer) {
        throw new Error('Invalid response format');
      }

      // Get pagination data from data attributes
      const total = parseInt(dataContainer.dataset.total, 10) || 0;
      const perPage = parseInt(dataContainer.dataset.perPage, 10) || 24;

      // Calculate total pages
      this.totalPages = Math.ceil(total / perPage);

      // Get product cards from the response
      const cards = dataContainer.querySelectorAll('.bundle-card');

      if (cards.length === 0 && this.currentPage === 1) {
        this.productsGrid.innerHTML = '<p class="bundle-no-products">Aucun produit disponible</p>';
        this.paginationContainer.style.display = 'none';
      } else {
        // Render the HTML cards (always replace for pagination)
        this.renderProductCards(cards, true);

        // Update pagination UI
        this.updatePaginationUI();
      }
    } catch (error) {
      console.error('[BundleBuilder] Load error:', error);
      this.showError('Erreur de chargement');
    } finally {
      this.isLoading = false;
    }
  }

  updatePaginationUI() {
    if (!this.paginationContainer) return;

    const prevBtn = this.paginationContainer.querySelector('.bundle-pagination-prev');
    const nextBtn = this.paginationContainer.querySelector('.bundle-pagination-next');
    const currentEl = this.paginationContainer.querySelector('.bundle-pagination-current');
    const totalEl = this.paginationContainer.querySelector('.bundle-pagination-total');

    // Update page numbers
    if (currentEl) currentEl.textContent = this.currentPage;
    if (totalEl) totalEl.textContent = this.totalPages;

    // Update button states
    if (prevBtn) prevBtn.disabled = this.currentPage <= 1;
    if (nextBtn) nextBtn.disabled = this.currentPage >= this.totalPages;

    // Show pagination if more than one page
    this.paginationContainer.style.display = this.totalPages > 1 ? 'flex' : 'none';
  }

  loadMoreProducts() {
    if (this.isLoading || !this.hasMoreProducts) return;

    const btn = this.loadMoreContainer.querySelector('.bundle-load-more');
    const textEl = btn.querySelector('.bundle-load-more-text');
    const loadingEl = btn.querySelector('.bundle-load-more-loading');

    btn.disabled = true;
    textEl.style.display = 'none';
    loadingEl.style.display = 'inline-flex';

    this.currentPage++;
    this.loadProducts().finally(() => {
      btn.disabled = false;
      textEl.style.display = 'inline';
      loadingEl.style.display = 'none';
    });
  }

  renderProducts(products, clear = false) {
    if (clear) {
      this.productsGrid.innerHTML = '';
    }

    // Filter out the current pack product from the list
    const filteredProducts = products.filter(product => {
      return String(product.id) !== String(this.currentProductId);
    });

    filteredProducts.forEach(product => {
      const card = this.createProductCard(product);
      this.productsGrid.appendChild(card);
    });
  }

  renderProductCards(cards, clear = false) {
    if (clear) {
      this.productsGrid.innerHTML = '';
    }

    // Convert NodeList to Array and filter out current pack product + items already in cart
    let filteredCount = 0;
    Array.from(cards).forEach(card => {
      const productId = card.dataset.productId;
      const variantId = card.dataset.variantId;

      // Skip the current pack product
      if (String(productId) === String(this.currentProductId)) {
        return;
      }

      // Skip products already in cart
      if (this.isInCart(variantId)) {
        filteredCount++;
        return;
      }

      // Clone the card to move it to our DOM
      const clonedCard = card.cloneNode(true);

      // Track product data for summary (variantId already defined above)
      if (variantId) {
        // Only add to allProducts if not already tracked (avoid duplicates across pages)
        const existingProduct = this.allProducts.find(p => p.variantId === variantId);
        if (!existingProduct) {
          this.allProducts.push({
            variantId,
            productId,
            handle: clonedCard.dataset.handle,
            title: clonedCard.dataset.title,
            price: parseInt(clonedCard.dataset.price, 10) || 0,
            image: clonedCard.dataset.image
          });
        }

        // Re-apply selected state if this product is in selectedItems
        if (this.selectedItems.has(variantId)) {
          clonedCard.classList.add('bundle-card--selected');
          // Show the overlay
          const overlay = this.createSelectedOverlay();
          const imageContainer = clonedCard.querySelector('.product-card-image, .bundle-card-image');
          if (imageContainer) {
            imageContainer.appendChild(overlay);
          }
        }
      }

      this.productsGrid.appendChild(clonedCard);

      // Set up hover images for this card
      this.setupCardHoverImages(clonedCard);
    });

    if (filteredCount > 0) {
      console.log('[BundleBuilder] Filtered out', filteredCount, 'products already in cart');
    }

    // Trigger lazy loading for new images
    this.triggerLazyLoad();
  }

  setupCardHoverImages(card) {
    const images = card.querySelectorAll('.product-secondary-image');
    const nav = card.querySelectorAll('.product-secondary-images-nav li');
    const imageContainer = card.querySelector('.product-card-image');

    if (!images.length || !imageContainer) return;

    const imageLength = images.length;

    const mousemove = function(e) {
      const l = e.offsetX;
      const w = this.getBoundingClientRect().width;
      const prc = l / w;
      const sel = Math.floor(prc * imageLength);
      const selimg = images[sel];

      images.forEach((image, index) => {
        if (image.classList.contains('hover')) {
          image.classList.remove('hover');
          if (nav.length && nav[index]) {
            nav[index].classList.remove('active');
          }
        }
      });

      if (selimg && !selimg.classList.contains('hover')) {
        selimg.classList.add('hover');
        if (nav.length && nav[sel]) {
          nav[sel].classList.add('active');
        }
      }
    };

    const mouseleave = function() {
      images.forEach((image, index) => {
        image.classList.remove('hover');
        if (nav.length && nav[index]) {
          nav[index].classList.remove('active');
        }
      });
    };

    imageContainer.addEventListener('mouseenter', mousemove, { passive: true });
    imageContainer.addEventListener('mousemove', mousemove, { passive: true });
    imageContainer.addEventListener('mouseleave', mouseleave, { passive: true });
    imageContainer.addEventListener('touchstart', mousemove, { passive: true });
    imageContainer.addEventListener('touchmove', mousemove, { passive: true });
    imageContainer.addEventListener('touchend', mouseleave, { passive: true });
  }

  triggerLazyLoad() {
    // Trigger lazysizes to process new images using unveil() like rest of theme
    if (typeof window.lazySizes !== 'undefined') {
      // Unveil all lazyload images in the products grid
      this.productsGrid.querySelectorAll('img.lazyload').forEach((image) => {
        lazySizes.loader.unveil(image);
      });

      // Also check any elements that might need auto-sizing
      window.lazySizes.autoSizer.checkElems();
    }

    // Fallback: Force srcset loading for any remaining lazyload images
    requestAnimationFrame(() => {
      const lazyImages = this.productsGrid.querySelectorAll('img.lazyload');
      lazyImages.forEach(img => {
        if (img.dataset.srcset && !img.srcset) {
          img.srcset = img.dataset.srcset;
        }
      });
    });
  }

  createProductCard(product) {
    // Use variant data from API (variant_available, variant_id)
    const isAvailable = product.variant_available !== false && product.variant_available !== null;

    // Use clean_image (metafield) as primary, fallback to featured_image
    const primaryImage = product.clean_image || product.featured_image || '';

    // Hover images: if clean_image is used, show all product images; otherwise skip first
    const hoverImages = product.images || [];
    const hasHoverImages = hoverImages.length > 0;

    // Price from API is in store currency (euros), convert to cents for consistent formatting
    const priceRaw = parseFloat(product.price || 0);
    const price = Math.round(priceRaw * 100);

    const card = document.createElement('div');
    card.className = `bundle-card${!isAvailable ? ' bundle-card--sold-out' : ''}`;
    card.dataset.variantId = product.variant_id;
    card.dataset.productId = product.id;
    card.dataset.handle = product.handle;
    card.dataset.price = price;
    card.dataset.title = product.title;
    card.dataset.image = primaryImage;
    card.dataset.available = isAvailable;

    // Build hover images HTML (max 4 secondary images)
    let hoverImagesHtml = '';
    if (hasHoverImages) {
      const maxHoverImages = 4;
      const imagesToShow = hoverImages.slice(0, maxHoverImages);
      hoverImagesHtml = imagesToShow.map(imgUrl =>
        `<img src="${imgUrl}" alt="${this.escapeHtml(product.title)}" class="bundle-card-img bundle-card-img--secondary" loading="lazy">`
      ).join('');
    }

    card.innerHTML = `
      <figure class="bundle-card-image${hasHoverImages ? ' thb-hover' : ''}">
        <a href="/products/${product.handle}" class="bundle-card-image-link aspect-ratio" style="--padding-bottom: 100%;">
          ${primaryImage ? `<img src="${primaryImage}" alt="${this.escapeHtml(product.title)}" class="bundle-card-img bundle-card-img--primary" loading="lazy">` : '<div class="thb-placeholder"></div>'}
          ${hoverImagesHtml}
        </a>
        <div class="bundle-card-selected-badge" style="display: none;">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
      </figure>
      <div class="bundle-card-info">
        <a href="/products/${product.handle}" class="bundle-card-title">${this.escapeHtml(product.title)}</a>
        <div class="bundle-card-price">${this.formatPrice(price)}</div>
        ${!isAvailable ? '<div class="bundle-card-sold-out">Rupture de stock</div>' : ''}
      </div>
      <div class="bundle-card-actions">
        <button type="button" class="bundle-card-toggle button small" ${!isAvailable ? 'disabled' : ''}>
          <span class="bundle-card-add-text">Ajouter</span>
          <span class="bundle-card-remove-text" style="display: none;">Retirer</span>
        </button>
        <quick-view class="bundle-card-quickview button small outline" data-product-handle="${product.handle}" tabindex="0">
          <svg class="thb-quick-view" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
          <span>Voir</span>
          <svg class="thb-loading" style="display:none" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>
        </quick-view>
      </div>
    `;

    return card;
  }

  toggleProduct(card) {
    const variantId = card.dataset.variantId;
    const isAvailable = card.dataset.available === 'true';

    if (!isAvailable) return;

    if (this.selectedItems.has(variantId)) {
      this.selectedItems.delete(variantId);
      card.classList.remove('bundle-card--selected');
      // Remove the overlay
      this.removeSelectionOverlay(card);

      // Track product remove
      if (window.bmTracking) {
        window.bmTracking.bundleProductRemove(variantId, this.selectedItems.size);
      }
    } else {
      const productData = {
        variantId,
        productId: card.dataset.productId,
        handle: card.dataset.handle,
        title: card.dataset.title,
        price: parseFloat(card.dataset.price) || 0,
        image: card.dataset.image
      };
      this.selectedItems.set(variantId, productData);
      card.classList.add('bundle-card--selected');
      // Add the overlay
      this.addSelectionOverlay(card);

      // Track product add
      if (window.bmTracking) {
        window.bmTracking.bundleProductAdd({
          id: productData.productId,
          title: productData.title,
          price: productData.price / 100
        }, this.selectedItems.size);
      }
    }

    this.updateSummary();
    this.updateTierHighlights();
    this.checkGiftEligibility();
  }

  /**
   * Fetch current cart to check for items already in cart
   * Called before loading products to enable pre-selection
   */
  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');

      const cart = await response.json();

      // Store variant IDs of items already in cart
      this.cartItems = cart.items.map(item => ({
        variantId: String(item.variant_id),
        productId: String(item.product_id),
        quantity: item.quantity
      }));

      console.log('[BundleBuilder] Cart items loaded:', this.cartItems.length);
    } catch (error) {
      console.error('[BundleBuilder] Failed to fetch cart:', error);
      this.cartItems = [];
    }
  }

  /**
   * Check if a variant is already in the user's cart
   */
  isInCart(variantId) {
    return this.cartItems.some(item => item.variantId === String(variantId));
  }

  /**
   * Handle preselection from URL query parameter
   * Called after products are loaded
   */
  handlePreselection() {
    if (!this.preselectedHandle) return;

    // Find the card with the matching handle
    const card = this.productsGrid?.querySelector(`[data-handle="${this.preselectedHandle}"]`);

    if (card) {
      // Select the product
      this.toggleProduct(card);

      // Scroll the card into view with a slight delay for smooth UX
      setTimeout(() => {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Add a brief highlight effect
        card.classList.add('bundle-card--preselected');
        setTimeout(() => card.classList.remove('bundle-card--preselected'), 2000);
      }, 300);

      // Clear the preselectedHandle so it doesn't re-select on pagination
      this.preselectedHandle = null;
    }
  }

  addSelectionOverlay(card) {
    // Find the image container (product-card-image for theme cards, bundle-card-image for custom cards)
    const imageContainer = card.querySelector('.product-card-image') || card.querySelector('.bundle-card-image');
    if (!imageContainer) return;

    // Remove any existing overlay first
    const existingOverlay = imageContainer.querySelector('.bundle-card-selected-overlay');
    if (existingOverlay) existingOverlay.remove();

    // Create and inject the overlay
    const overlay = document.createElement('div');
    overlay.className = 'bundle-card-selected-overlay';
    overlay.innerHTML = `
      <span class="bundle-card-selected-overlay-text">Sélectionnée</span>
      <button type="button" class="bundle-card-remove">Retirer</button>
    `;
    imageContainer.appendChild(overlay);
  }

  removeSelectionOverlay(card) {
    const overlay = card.querySelector('.bundle-card-selected-overlay');
    if (overlay) overlay.remove();
  }

  createSelectedOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'bundle-card-selected-overlay';
    overlay.innerHTML = `
      <span class="bundle-card-selected-overlay-text">Sélectionnée</span>
      <button type="button" class="bundle-card-remove">Retirer</button>
    `;
    return overlay;
  }

  removeProduct(variantId) {
    this.selectedItems.delete(variantId);

    const card = this.querySelector(`.bundle-card[data-variant-id="${variantId}"]`);
    if (card) {
      card.classList.remove('bundle-card--selected');
      this.removeSelectionOverlay(card);
    }

    this.updateSummary();
    this.updateTierHighlights();
    this.checkGiftEligibility();
  }

  async loadGiftProducts() {
    if (this.isGiftLoading) return;

    // Use the same collection as main products
    const collectionToUse = this.collectionHandle;

    if (!collectionToUse) {
      if (this.giftGrid) {
        this.giftGrid.innerHTML = '<p class="bundle-no-products">Aucune collection configurée</p>';
      }
      return;
    }

    this.isGiftLoading = true;
    console.log('[BundleBuilder] Loading ALL gift products from collection:', collectionToUse);

    // Show loading state
    if (this.giftGrid) {
      this.giftGrid.innerHTML = `
        <div class="bundle-loading">
          <div class="bundle-loading-spinner">
            <svg class="thb-loading" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2" opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/></path></svg>
          </div>
          <span>Chargement des cadeaux...</span>
        </div>
      `;
    }

    try {
      // Fetch ALL pages to get all eligible gift products
      const allCards = [];
      let page = 1;
      let hasMore = true;
      const maxPages = 20; // Safety limit

      while (hasMore && page <= maxPages) {
        const url = `/collections/${collectionToUse}?section_id=bundle-gift-products-html&page=${page}`;
        const response = await fetch(url);

        if (!response.ok) throw new Error('Failed to load gift products');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const productsData = doc.querySelector('.bundle-products-data');

        if (!productsData) {
          break;
        }

        const cards = productsData.querySelectorAll('product-card');
        const total = parseInt(productsData.dataset.total, 10) || 0;
        const perPage = parseInt(productsData.dataset.perPage, 10) || 24;

        cards.forEach(card => allCards.push(card));

        // Check if there are more pages
        const totalPages = Math.ceil(total / perPage);
        hasMore = page < totalPages;
        page++;
      }

      console.log('[BundleBuilder] Fetched', allCards.length, 'total products from', page - 1, 'pages');

      // Filter cards:
      // 1. Remove products already in cart
      // 2. Remove already selected products (the 10 bundle items)
      // 3. Keep only products with price <= 20€ (2000 cents)
      const selectedVariantIds = new Set(this.selectedItems.keys());
      const maxGiftPrice = 2000; // 20€ in cents

      const filteredCards = allCards.filter(card => {
        const variantId = card.dataset.variantId;
        const price = parseInt(card.dataset.price, 10) || 0;

        // Exclude products already in cart
        if (this.isInCart(variantId)) return false;

        // Exclude already selected products (bundle items)
        if (selectedVariantIds.has(variantId)) return false;

        // Only include products <= 20€
        if (price > maxGiftPrice) return false;

        // Exclude current pack product
        if (String(card.dataset.productId) === String(this.currentProductId)) return false;

        return true;
      });

      console.log('[BundleBuilder] Gift products filtered:', {
        total: allCards.length,
        eligible: filteredCards.length,
        selectedCount: selectedVariantIds.size,
        maxPrice: maxGiftPrice
      });

      // Store all filtered cards for pagination
      this.allGiftCards = filteredCards;
      this.giftPerPage = 12; // Show 12 gift cards per page
      this.giftTotalProducts = filteredCards.length;
      this.giftTotalPages = Math.ceil(filteredCards.length / this.giftPerPage);
      this.giftCurrentPage = 1;

      // Render first page
      this.renderGiftPage();

    } catch (error) {
      console.error('[BundleBuilder] Gift load error:', error);
      if (this.giftGrid) {
        this.giftGrid.innerHTML = '<p class="bundle-no-products">Erreur de chargement des cadeaux</p>';
      }
    } finally {
      this.isGiftLoading = false;
    }
  }

  renderGiftPage() {
    if (!this.giftGrid || !this.allGiftCards) return;

    const start = (this.giftCurrentPage - 1) * this.giftPerPage;
    const end = start + this.giftPerPage;
    const pageCards = this.allGiftCards.slice(start, end);

    if (pageCards.length === 0) {
      this.giftGrid.innerHTML = '<p class="bundle-no-products">Aucune voiture offerte disponible (produits ≤20€ non sélectionnés)</p>';
      if (this.giftPaginationContainer) {
        this.giftPaginationContainer.style.display = 'none';
      }
      return;
    }

    // Clear and add page cards
    this.giftGrid.innerHTML = '';
    pageCards.forEach(card => {
      this.giftGrid.appendChild(card.cloneNode(true));
    });

    // Re-apply selection state if gift was already selected
    if (this.selectedGift) {
      const selectedCard = this.giftGrid.querySelector(`[data-variant-id="${this.selectedGift.variantId}"]`);
      if (selectedCard) {
        selectedCard.classList.add('bundle-card--selected');
        this.addSelectionOverlay(selectedCard);
      }
    }

    // Bind click events to new gift cards
    this.bindGiftCardEvents();

    // Update pagination UI
    this.updateGiftPaginationUI();
  }

  bindGiftCardEvents() {
    if (!this.giftGrid) return;

    this.giftGrid.querySelectorAll('.bundle-card--gift').forEach(card => {
      const selectBtn = card.querySelector('.bundle-gift-select');
      if (selectBtn) {
        selectBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.selectGift(card);
        });
      }
    });
  }

  updateGiftPaginationUI() {
    if (!this.giftPaginationContainer) return;

    const prevBtn = this.giftPaginationContainer.querySelector('.bundle-pagination-prev');
    const nextBtn = this.giftPaginationContainer.querySelector('.bundle-pagination-next');
    const currentEl = this.giftPaginationContainer.querySelector('.bundle-pagination-current');
    const totalEl = this.giftPaginationContainer.querySelector('.bundle-pagination-total');

    // Update page numbers
    if (currentEl) currentEl.textContent = this.giftCurrentPage;
    if (totalEl) totalEl.textContent = this.giftTotalPages;

    // Update button states
    if (prevBtn) prevBtn.disabled = this.giftCurrentPage <= 1;
    if (nextBtn) nextBtn.disabled = this.giftCurrentPage >= this.giftTotalPages;

    // Show pagination if more than one page
    this.giftPaginationContainer.style.display = this.giftTotalPages > 1 ? 'flex' : 'none';
  }

  renderGiftProducts(products, clear = false) {
    if (clear) {
      this.giftGrid.innerHTML = '';
    }

    products.forEach(product => {
      const card = this.createGiftCard(product);
      this.giftGrid.appendChild(card);
    });
  }

  createGiftCard(product) {
    const variant = product.variants?.[0];
    // JSON API: product.available can be null, check variant.available instead
    const isAvailable = variant?.available === true;
    const imageUrl = this.getProductImage(product, 200);
    const originalPrice = variant?.price ? this.formatPrice(parseFloat(variant.price) * 100) : '';

    const card = document.createElement('div');
    card.className = `bundle-card bundle-card--gift${!isAvailable ? ' bundle-card--sold-out' : ''}`;
    card.dataset.variantId = variant?.id;
    card.dataset.productId = product.id;
    card.dataset.handle = product.handle;
    card.dataset.title = product.title;
    card.dataset.image = imageUrl;
    card.dataset.available = isAvailable;
    card.dataset.price = variant?.price ? parseFloat(variant.price) * 100 : 0;

    card.innerHTML = `
      <div class="bundle-card-image">
        <a href="/products/${product.handle}" class="bundle-card-image-link aspect-ratio" style="--padding-bottom: 100%;">
          ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" class="bundle-card-img" loading="lazy">` : '<div class="thb-placeholder"></div>'}
        </a>
        <div class="bundle-card-selected-badge" style="display: none;">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
      </div>
      <div class="bundle-card-info">
        <a href="/products/${product.handle}" class="bundle-card-title">${this.escapeHtml(product.title)}</a>
        <div class="bundle-card-price bundle-gift-price">
          <s class="bundle-gift-price-original">${originalPrice}</s>
          <span class="bundle-gift-price-free">0€</span>
        </div>
        ${!isAvailable ? '<div class="bundle-card-sold-out">Rupture de stock</div>' : ''}
      </div>
      <div class="bundle-card-actions">
        <button type="button" class="bundle-card-select button small" ${!isAvailable ? 'disabled' : ''}>
          <span class="bundle-card-select-text">Choisir</span>
          <span class="bundle-card-selected-text" style="display: none;">Sélectionné</span>
        </button>
      </div>
    `;

    return card;
  }

  selectGift(card) {
    const variantId = card.dataset.variantId;
    const isAvailable = card.dataset.available === 'true';

    if (!isAvailable) return;

    // Remove selection from previously selected gift cards
    this.giftGrid.querySelectorAll('.bundle-card--gift.bundle-card--selected').forEach(c => {
      c.classList.remove('bundle-card--selected');
      this.removeSelectionOverlay(c);
    });

    card.classList.add('bundle-card--selected');
    // Add the same overlay as regular product cards
    this.addSelectionOverlay(card);

    this.selectedGift = {
      variantId,
      productId: card.dataset.productId,
      handle: card.dataset.handle,
      title: card.dataset.title,
      image: card.dataset.image,
      price: parseFloat(card.dataset.price) || 0
    };

    this.updateGiftSelected();
    this.showGiftPrompt(false);
    this.updateSummary();
  }

  deselectGift(card) {
    card.classList.remove('bundle-card--selected');
    this.removeSelectionOverlay(card);
    this.selectedGift = null;
    this.updateGiftSelected();
    this.showGiftPrompt(true);
    this.updateSummary();
  }

  scrollToProductsGrid() {
    const target = this.productsCollapsible || this.productsGrid;
    if (target) {
      const headerOffset = 120; // Account for sticky header
      const elementPosition = target.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  }

  scrollToGiftGrid() {
    if (this.giftCollapsible) {
      const headerOffset = 120;
      const elementPosition = this.giftCollapsible.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  }

  checkGiftEligibility() {
    const count = this.selectedItems.size;
    // Unlock at 10+ products (gift collection is optional for unlock behavior)
    const isUnlocked = count >= 10;

    // Update the gift counter text
    const giftCounter = this.giftCollapsible?.querySelector('.bundle-collapsible-gift-counter');
    if (giftCounter) {
      if (isUnlocked) {
        giftCounter.style.display = 'none';
      } else {
        const remaining = 10 - count;
        const word = remaining === 1 ? 'voiture' : 'voitures';
        giftCounter.textContent = `(choisissez encore ${remaining} ${word})`;
        giftCounter.style.display = '';
      }
    }

    console.log('[BundleBuilder] checkGiftEligibility:', { count, isUnlocked, giftCollapsible: !!this.giftCollapsible, productsCollapsible: !!this.productsCollapsible });

    // Show/hide gift prompt in summary regardless of gift collapsible
    if (isUnlocked) {
      this.showGiftPrompt(true);
    } else {
      this.showGiftPrompt(false);
    }

    if (!this.giftCollapsible) return;

    if (isUnlocked) {
      // Unlock the gift section
      this.giftCollapsible.classList.remove('bundle-collapsible--locked');
      this.giftCollapsible.classList.add('bundle-collapsible--unlocked');

      // Hide the locked overlay
      if (this.giftLockedOverlay) {
        this.giftLockedOverlay.style.display = 'none';
      }

      // Refresh gift products to exclude currently selected items
      this.loadGiftProducts();

      // Auto-open gift and close products when unlocked for the first time
      if (!this.giftCollapsible.hasAttribute('data-was-unlocked')) {
        console.log('[BundleBuilder] First time unlock - closing products, opening gift');
        // Close products collapsible
        if (this.productsCollapsible) {
          this.productsCollapsible.open = false;
        }
        // Open gift collapsible
        this.giftCollapsible.open = true;
        this.giftCollapsible.setAttribute('data-was-unlocked', 'true');
        // Scroll to gift section
        setTimeout(() => this.scrollToGiftGrid(), 150);
      }
    } else {
      // Lock the gift section
      this.giftCollapsible.classList.add('bundle-collapsible--locked');
      this.giftCollapsible.classList.remove('bundle-collapsible--unlocked');

      // Show the locked overlay
      if (this.giftLockedOverlay) {
        this.giftLockedOverlay.style.display = 'flex';
      }

      // Clear selection if dropping below 10
      if (this.selectedGift) {
        this.selectedGift = null;
        this.giftGrid?.querySelectorAll('.bundle-card--selected').forEach(c => {
          c.classList.remove('bundle-card--selected');
          this.removeSelectionOverlay(c);
        });
        this.updateGiftSelected();
      }
    }
  }

  showGiftPrompt(show) {
    if (!this.summaryContainer) return;

    let promptEl = this.summaryContainer.querySelector('.bundle-gift-prompt');

    if (show && !this.selectedGift) {
      if (!promptEl) {
        promptEl = document.createElement('div');
        promptEl.className = 'bundle-gift-prompt';
        promptEl.innerHTML = `
          <span class="bundle-gift-prompt-icon">🎁</span>
          <span class="bundle-gift-prompt-text">Sélectionnez votre voiture offerte</span>
        `;
        // Insert before the totals section
        const totalsEl = this.summaryContainer.querySelector('.bundle-totals');
        if (totalsEl) {
          totalsEl.parentNode.insertBefore(promptEl, totalsEl);
        }
      }
      promptEl.style.display = 'flex';
    } else if (promptEl) {
      promptEl.style.display = 'none';
    }
  }

  updateGiftSelected() {
    if (!this.giftSelectedContainer) return;

    if (this.selectedGift) {
      this.giftSelectedContainer.style.display = 'block';
      const priceDisplay = this.selectedGift.price > 0
        ? `<s style="color: #999; font-size: 0.85em;">${this.formatPrice(this.selectedGift.price)}</s> <span style="color: #0e5757; font-weight: 600;">OFFERT</span>`
        : '<span style="color: #0e5757; font-weight: 600;">OFFERT</span>';
      this.giftSelectedContainer.querySelector('.bundle-gift-selected-item').innerHTML = `
        <img src="${this.selectedGift.image}" alt="${this.escapeHtml(this.selectedGift.title)}" class="bundle-selected-item-image">
        <div class="bundle-selected-item-info">
          <div class="bundle-selected-item-title">${this.escapeHtml(this.selectedGift.title)}</div>
          <div class="bundle-selected-item-price">${priceDisplay}</div>
        </div>
      `;
    } else {
      this.giftSelectedContainer.style.display = 'none';
    }
  }

  getCurrentTier() {
    const count = this.selectedItems.size;

    for (let i = this.discountTiers.length - 1; i >= 0; i--) {
      if (count >= this.discountTiers[i].quantity) {
        return this.discountTiers[i];
      }
    }

    return null;
  }

  calculateTotal() {
    let subtotal = 0;
    this.selectedItems.forEach(item => {
      subtotal += item.price;
    });

    const count = this.selectedItems.size;

    // Money discount based on quantity
    let moneyDiscount = 0;
    if (count >= 5) {
      moneyDiscount = 10 * 100; // -10€
    } else if (count >= 3) {
      moneyDiscount = 5 * 100; // -5€
    }

    // Gift discount (when 10+ items and gift selected)
    let giftDiscount = 0;
    if (count >= 10 && this.selectedGift && this.selectedGift.price > 0) {
      giftDiscount = this.selectedGift.price;
    }

    const totalDiscount = moneyDiscount + giftDiscount;

    return {
      subtotal,
      moneyDiscount,
      giftDiscount,
      discount: totalDiscount,
      total: Math.max(0, subtotal - moneyDiscount)
    };
  }

  updateSummary() {
    if (!this.summaryContainer) return;

    const count = this.selectedItems.size;
    const { subtotal, moneyDiscount, giftDiscount, discount, total } = this.calculateTotal();
    const tier = this.getCurrentTier();

    const progressCount = this.summaryContainer.querySelector('.bundle-progress-count');
    const currentTier = this.summaryContainer.querySelector('.bundle-current-tier');
    const currentTierValue = this.summaryContainer.querySelector('.bundle-current-tier-value');
    const subtotalEl = this.summaryContainer.querySelector('.bundle-subtotal-value');
    const discountEl = this.summaryContainer.querySelector('.bundle-discount');
    const discountLabel = this.summaryContainer.querySelector('.bundle-discount-label');
    const discountValue = this.summaryContainer.querySelector('.bundle-discount-value');
    const totalEl = this.summaryContainer.querySelector('.bundle-total-value');
    const addToCartBtn = this.summaryContainer.querySelector('.bundle-add-to-cart');
    const noSelectionEl = this.summaryContainer.querySelector('.bundle-no-selection');

    if (progressCount) progressCount.textContent = count;

    // Update the rewards tracker
    this.updateRewardsTracker(count, subtotal);

    // Show the money discount (-5€ or -10€), separate from gift
    if (count >= 5) {
      currentTier.style.display = 'flex';
      currentTierValue.textContent = '-10€';
    } else if (count >= 3) {
      currentTier.style.display = 'flex';
      currentTierValue.textContent = '-5€';
    } else {
      currentTier.style.display = 'none';
    }

    // Show free shipping when subtotal >= 75€
    const freeShippingEl = this.summaryContainer.querySelector('.bundle-free-shipping');
    if (freeShippingEl) {
      freeShippingEl.style.display = subtotal >= 7500 ? 'flex' : 'none';
    }

    subtotalEl.textContent = this.formatPrice(subtotal);

    // Show total savings (money discount + gift value)
    if (discount > 0) {
      discountEl.style.display = 'flex';
      if (giftDiscount > 0) {
        discountLabel.innerHTML = '<strong style="color: #0e5757;">🎁 Total économisé</strong>';
        discountValue.innerHTML = `<strong style="color: #0e5757; font-size: 1.1em;">-${this.formatPrice(discount)}</strong>`;
      } else {
        discountLabel.textContent = 'Vous économisez';
        discountValue.textContent = `-${this.formatPrice(discount)}`;
      }
    } else {
      discountEl.style.display = 'none';
    }

    totalEl.textContent = this.formatPrice(total);

    const canAddToCart = count >= this.minItems && (count < 10 || this.selectedGift);
    addToCartBtn.disabled = !canAddToCart;

    this.renderSelectedItems();

    if (noSelectionEl) {
      noSelectionEl.style.display = count === 0 ? 'block' : 'none';
    }

    // Update mobile bar
    this.updateMobileBar();
  }

  renderSelectedItems() {
    if (!this.selectedItemsContainer) return;

    const items = Array.from(this.selectedItems.values());

    if (items.length === 0) {
      this.selectedItemsContainer.innerHTML = `
        <p class="bundle-no-selection">Sélectionnez minimum ${this.minItems} produits</p>
      `;
      return;
    }

    this.selectedItemsContainer.innerHTML = items.map(item => `
      <div class="bundle-selected-item">
        <img src="${item.image}" alt="${this.escapeHtml(item.title)}" class="bundle-selected-item-image">
        <div class="bundle-selected-item-info">
          <div class="bundle-selected-item-title">${this.escapeHtml(item.title)}</div>
          <div class="bundle-selected-item-price">${this.formatPrice(item.price)}</div>
        </div>
        <button type="button" class="bundle-selected-item-remove" data-variant-id="${item.variantId}" aria-label="Retirer">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    `).join('');
  }

  setupMobileSummaryToggle() {
    const mobileBar = this.summaryContainer?.querySelector('.bundle-summary-mobile-bar');
    if (!mobileBar) return;

    mobileBar.addEventListener('click', () => {
      this.summaryContainer.classList.toggle('mobile-collapsed');
    });

    // Initialize mobile milestones and progress bar on load
    const count = this.selectedItems.size;
    const { subtotal } = this.calculateTotal();
    this.updateMobileMilestones(count, subtotal);
    this.updateMobileProgressBar(count, subtotal);
  }

  updateMobileBar() {
    if (!this.summaryContainer) return;

    const mobileCount = this.summaryContainer.querySelector('.bundle-summary-mobile-count');
    const mobileTotal = this.summaryContainer.querySelector('.bundle-summary-mobile-total');
    const count = this.selectedItems.size;
    const { subtotal, total } = this.calculateTotal();

    if (mobileCount) {
      mobileCount.textContent = count;
    }

    if (mobileTotal) {
      mobileTotal.textContent = this.formatPrice(total);
    }

    // Update mobile milestone icons
    this.updateMobileMilestones(count, subtotal);

    // Update mobile progress bar
    this.updateMobileProgressBar(count, subtotal);

    // Enable/disable mobile add button based on count and gift selection
    // Button is ALWAYS VISIBLE on mobile, just disabled when not enough items
    const canAddToCart = count >= this.minItems && (count < 10 || this.selectedGift);

    if (this.mobileAddBtn) {
      // Always keep button visible, just toggle disabled state
      this.mobileAddBtn.classList.remove('bundle-mobile-add-btn--hidden');
      this.mobileAddBtn.disabled = !canAddToCart;
    }
  }

  updateMobileProgressBar(count, subtotal) {
    const progressText = this.summaryContainer?.querySelector('.bundle-mobile-progress-text');
    const progressFill = this.summaryContainer?.querySelector('.bundle-mobile-progress-fill');
    if (!progressText || !progressFill) return;

    // Calculate progress towards next milestone
    // Milestones: 3 products (-5€), 75€ (shipping), 5 products (-10€), 10 products (gift)
    let progressPercent = 0;
    let message = '';

    if (count < 3) {
      // Progress towards 3 products
      progressPercent = (count / 3) * 25;
      const remaining = 3 - count;
      message = `Plus que <strong>${remaining} produit${remaining > 1 ? 's' : ''}</strong> pour <strong>-5€</strong>`;
    } else if (subtotal < 7500) {
      // Progress towards free shipping (75€)
      progressPercent = 25 + ((subtotal / 7500) * 25);
      const remainingEuros = Math.ceil((7500 - subtotal) / 100);
      message = `Plus que <strong>${remainingEuros}€</strong> pour la <strong>Livraison gratuite</strong>`;
    } else if (count < 5) {
      // Progress towards 5 products
      progressPercent = 50 + ((count - 3) / 2) * 25;
      const remaining = 5 - count;
      message = `Plus que <strong>${remaining} produit${remaining > 1 ? 's' : ''}</strong> pour <strong>-10€</strong>`;
    } else if (count < 10) {
      // Progress towards 10 products (gift)
      progressPercent = 75 + ((count - 5) / 5) * 25;
      const remaining = 10 - count;
      message = `Plus que <strong>${remaining} produit${remaining > 1 ? 's' : ''}</strong> pour le <strong>Cadeau</strong>`;
    } else {
      // All milestones reached
      progressPercent = 100;
      message = `<strong>Félicitations !</strong> Tous les avantages débloqués 🎉`;
    }

    progressText.innerHTML = message;
    progressFill.style.width = `${Math.min(progressPercent, 100)}%`;
  }

  updateMobileMilestones(count, subtotal) {
    const milestones = this.summaryContainer?.querySelectorAll('.bundle-mobile-milestone');
    if (!milestones) return;

    // Define milestone targets
    const targets = [
      { milestone: 3, type: 'quantity' },
      { milestone: 7500, type: 'price' },
      { milestone: 5, type: 'quantity' },
      { milestone: 10, type: 'quantity' }
    ];

    let nextFound = false;

    milestones.forEach((el, index) => {
      const target = targets[index];
      if (!target) return;

      const value = target.type === 'quantity' ? count : subtotal;
      const isReached = value >= target.milestone;

      el.classList.remove('reached', 'next');

      if (isReached) {
        el.classList.add('reached');
      } else if (!nextFound) {
        el.classList.add('next');
        nextFound = true;
      }
    });
  }

  updateTierHighlights() {
    const count = this.selectedItems.size;
    const tierElements = document.querySelectorAll('.bundle-tier');

    tierElements.forEach(el => {
      const tierValue = parseInt(el.dataset.tier, 10);
      if (count >= tierValue) {
        el.classList.add('bundle-tier--active');
      } else {
        el.classList.remove('bundle-tier--active');
      }
    });
  }

  /**
   * Update the rewards tracker progress bar and milestone states
   * @param {number} count - Number of selected items
   * @param {number} subtotal - Total price in cents
   */
  updateRewardsTracker(count, subtotal) {
    const tracker = this.summaryContainer?.querySelector('.bundle-rewards-tracker');
    if (!tracker) return;

    const progressBar = tracker.querySelector('.bundle-rewards-bar-fill');
    const nextRewardEl = tracker.querySelector('.bundle-rewards-next');
    const milestones = tracker.querySelectorAll('.bundle-milestone');

    // Define milestones with their thresholds
    // Order: 3 products, 75€ shipping, 5 products, 10 products
    const milestonesData = [
      { type: 'quantity', value: 3, label: '-5€', position: 25 },
      { type: 'price', value: 7500, label: 'Livraison gratuite', position: 50 },
      { type: 'quantity', value: 5, label: '-10€', position: 75 },
      { type: 'quantity', value: 10, label: 'Voiture offerte', position: 100 }
    ];

    let reachedCount = 0;
    let nextMilestone = null;

    // Update each milestone's state
    milestones.forEach((milestone, index) => {
      const data = milestonesData[index];
      if (!data) return;

      let isReached = false;
      if (data.type === 'quantity') {
        isReached = count >= data.value;
      } else if (data.type === 'price') {
        isReached = subtotal >= data.value;
      }

      // Reset classes
      milestone.classList.remove('bundle-milestone--reached', 'bundle-milestone--next');

      if (isReached) {
        milestone.classList.add('bundle-milestone--reached');
        reachedCount++;

        // Track milestone reached (only once per milestone)
        const milestoneKey = `${data.type}_${data.value}`;
        if (!this.reachedMilestones.has(milestoneKey)) {
          this.reachedMilestones.add(milestoneKey);
          if (window.bmTracking) {
            window.bmTracking.bundleMilestone(data.value, data.label, count);
          }
        }
      } else if (!nextMilestone) {
        milestone.classList.add('bundle-milestone--next');
        nextMilestone = data;
      }
    });

    // Calculate progress bar width based on reached milestones
    let progressPercent = 0;
    if (reachedCount > 0) {
      // Progress to the last reached milestone's position
      progressPercent = milestonesData[reachedCount - 1].position;

      // Add partial progress toward next milestone
      if (nextMilestone && reachedCount < milestonesData.length) {
        const currentPos = milestonesData[reachedCount - 1].position;
        const nextPos = milestonesData[reachedCount].position;
        const range = nextPos - currentPos;

        let partialProgress = 0;
        if (nextMilestone.type === 'quantity') {
          const prevValue = milestonesData[reachedCount - 1].type === 'quantity'
            ? milestonesData[reachedCount - 1].value
            : Math.ceil(milestonesData[reachedCount - 1].value / 2000); // Estimate products from price
          const remaining = nextMilestone.value - prevValue;
          const current = count - prevValue;
          partialProgress = Math.min(1, Math.max(0, current / remaining));
        } else if (nextMilestone.type === 'price') {
          const prevValue = reachedCount > 0 && milestonesData[reachedCount - 1].type === 'price'
            ? milestonesData[reachedCount - 1].value
            : 0;
          const remaining = nextMilestone.value - prevValue;
          const current = subtotal - prevValue;
          partialProgress = Math.min(1, Math.max(0, current / remaining));
        }

        progressPercent += partialProgress * range * 0.8; // 80% of the way to not overlap icon
      }
    } else if (count > 0) {
      // Progress toward first milestone (3 products)
      const firstMilestonePos = milestonesData[0].position;
      progressPercent = (count / milestonesData[0].value) * firstMilestonePos * 0.8;
    }

    // Update progress bar
    if (progressBar) {
      progressBar.style.width = `${Math.min(100, progressPercent)}%`;
    }

    // Update "next reward" text
    if (nextRewardEl) {
      if (nextMilestone) {
        if (nextMilestone.type === 'quantity') {
          const remaining = nextMilestone.value - count;
          const productWord = remaining === 1 ? 'produit' : 'produits';
          nextRewardEl.innerHTML = `Plus que <strong>${remaining} ${productWord}</strong> pour <strong>${nextMilestone.label}</strong>`;
          nextRewardEl.classList.remove('bundle-rewards-next--achieved');
        } else if (nextMilestone.type === 'price') {
          const remaining = Math.ceil((nextMilestone.value - subtotal) / 100);
          if (remaining > 0) {
            nextRewardEl.innerHTML = `Plus que <strong>${remaining}€</strong> pour la <strong>${nextMilestone.label}</strong>`;
            nextRewardEl.classList.remove('bundle-rewards-next--achieved');
          }
        }
      } else if (reachedCount === milestonesData.length) {
        nextRewardEl.innerHTML = '🎉 <strong>Tous les bonus débloqués !</strong>';
        nextRewardEl.classList.add('bundle-rewards-next--achieved');
      } else {
        nextRewardEl.innerHTML = '';
      }
    }
  }

  async handleMobileAddToCart() {
    const count = this.selectedItems.size;
    if (count < this.minItems) return;
    if (count >= 10 && !this.selectedGift) return;

    const btn = this.mobileAddBtn;
    if (!btn) return;

    const textEl = btn.querySelector('.bundle-mobile-add-btn-text');
    const loadingEl = btn.querySelector('.bundle-mobile-add-btn-loading');

    btn.disabled = true;
    if (textEl) textEl.style.display = 'none';
    if (loadingEl) loadingEl.style.display = 'inline-flex';

    // Call the main addToCart logic
    await this.addToCart();

    // Reset mobile button state (addToCart handles everything else)
    btn.disabled = false;
    if (textEl) textEl.style.display = 'inline';
    if (loadingEl) loadingEl.style.display = 'none';
  }

  async addToCart() {
    const count = this.selectedItems.size;

    if (count < this.minItems) return;
    if (count >= 10 && !this.selectedGift) return;

    const btn = this.summaryContainer?.querySelector('.bundle-add-to-cart');
    const textEl = btn?.querySelector('.bundle-add-to-cart-text');
    const loadingEl = btn?.querySelector('.bundle-add-to-cart-loading');

    if (btn) btn.disabled = true;
    if (textEl) textEl.style.display = 'none';
    if (loadingEl) loadingEl.style.display = 'inline-flex';

    const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    // Build items array for cart
    // Use variant IDs as-is (Shopify accepts both numbers and strings)
    const items = Array.from(this.selectedItems.values()).map(item => {
      console.log('[BundleBuilder] Item variantId:', item.variantId, 'type:', typeof item.variantId);
      return {
        id: item.variantId,
        quantity: 1,
        properties: {
          '_bundleId': bundleId,
          '_bundleType': 'theme-pack',
          'Pack': this.productTitle
        }
      };
    });

    // Add gift item if 10+ products selected
    if (this.selectedGift && count >= 10) {
      console.log('[BundleBuilder] Gift variantId:', this.selectedGift.variantId);
      items.push({
        id: this.selectedGift.variantId,
        quantity: 1,
        properties: {
          '_bundleId': bundleId,
          '_bundleType': 'theme-pack-gift',
          '_bundleGift': 'true',
          'Pack': this.productTitle,
          'Cadeau': 'Offert'
        }
      });
    }

    try {
      // Add items to cart
      // Discounts are handled automatically by Shopify Function based on _bundleId and _bundleGift properties
      console.log('[BundleBuilder] Adding items to cart:', items);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('[BundleBuilder] Cart add failed:', res.status, errorText);
        throw new Error(`Cart add failed: ${res.status} - ${errorText}`);
      }

      // Track bundle add to cart
      if (window.bmTracking) {
        const { subtotal, discount } = this.calculateTotal();
        window.bmTracking.bundleAddToCart(count, subtotal / 100, discount / 100);
      }

      if (textEl) textEl.textContent = 'Ajouté !';
      if (textEl) textEl.style.display = 'inline';
      if (loadingEl) loadingEl.style.display = 'none';

      setTimeout(() => {
        if (textEl) textEl.textContent = 'Ajouter le pack au panier';
        if (btn) btn.disabled = false;
      }, 2000);

      // Collapse mobile summary panel before opening cart drawer
      this.collapseMobileSummary();

      this.openCartDrawer();

      // Clear selection
      this.selectedItems.clear();
      this.selectedGift = null;
      this.querySelectorAll('.bundle-card--selected').forEach(c => {
        c.classList.remove('bundle-card--selected');
        this.removeSelectionOverlay(c);
      });
      this.updateSummary();
      this.updateTierHighlights();
      this.checkGiftEligibility();

    } catch (error) {
      console.error('[BundleBuilder] Add to cart error:', error);
      if (textEl) textEl.textContent = 'Erreur';
      if (textEl) textEl.style.display = 'inline';
      if (loadingEl) loadingEl.style.display = 'none';

      setTimeout(() => {
        if (textEl) textEl.textContent = 'Ajouter le pack au panier';
        if (btn) btn.disabled = false;
      }, 2000);
    }
  }

  collapseMobileSummary() {
    // Only hide on mobile (max-width: 1024px) - desktop summary should stay visible
    if (window.innerWidth > 1024) {
      return;
    }

    const container = this.summaryContainer?.closest('.bundle-summary-container')
      || document.querySelector('.bundle-summary-container');

    if (container) {
      container.classList.add('bundle-summary--hidden');
      container.style.setProperty('display', 'none', 'important');
      container.style.setProperty('visibility', 'hidden', 'important');
      container.style.setProperty('opacity', '0', 'important');
      container.style.setProperty('pointer-events', 'none', 'important');
    }

    document.body.classList.add('bundle-cart-opening');
  }

  openCartDrawer() {
    const cartDrawer = document.getElementById('Cart-Drawer');
    if (cartDrawer) {
      if (typeof cartDrawer.refresh === 'function') {
        cartDrawer.refresh();
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
      setTimeout(() => {
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      }, 300);
    }
  }

  getProductImage(product, size = 200) {
    const url = product.featured_image?.src || product.images?.[0]?.src || product.images?.[0];
    if (!url) return '';

    if (url.includes('cdn.shopify.com')) {
      return url.replace(/(_\d+x\d*)|(_\d+x)/, `_${size}x`).replace(/\.(\w+)(\?|$)/, `_${size}x.$1$2`);
    }
    return url;
  }

  formatPrice(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  showError(message) {
    if (this.productsGrid) {
      this.productsGrid.innerHTML = `
        <div class="bundle-error" style="padding: 40px; text-align: center; color: #666;">
          ${this.escapeHtml(message)}
        </div>
      `;
    }
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
